/**
 * Notes module shared fields and dialogs: note details / interest fields (create + edit wizard),
 * the maturity payout batch, and reject-with-reason.
 * Each dialog awaits its onSubmit and stays open on failure, showing the API's own message
 * (extractApiError) — no optimistic close, no generic "something went wrong".
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Grid, Alert,
  Typography, Table, TableBody, TableHead, TableRow, TableCell, InputAdornment,
} from '@mui/material';
import { apiGet, extractApiData, extractApiError } from '../../utils/apiClient';
import { cachedApiGet } from '../../utils/refDataCache';
import { formatNumber } from '../../utils/formatters';

const useSubmit = (onSubmit, onClose) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(extractApiError(err, 'The request failed'));
    } finally {
      setSaving(false);
    }
  };
  return { saving, error, setError, submit };
};

// -------------------------------------------------------------------------------------
// Note terms — shared by the create wizard (steps 1-2) and the Edit terms dialog
// -------------------------------------------------------------------------------------

const SPC = 'Simple Periodic Capitalization';

/**
 * Countries, frequencies (Adhoc excluded), issuers and calculation profiles; programmes follow
 * the chosen issuer. Profiles are the same list the contract wizard offers.
 */
export const useNoteRefData = (active, issuerId, onError, investmentTypeId = null) => {
  const [ref, setRef] = useState({ countries: [], frequencies: [], issuers: [], programs: [], profiles: [], investmentTypes: [] });
  useEffect(() => {
    if (!active) return;
    Promise.all([
      cachedApiGet('/refdata/countries'),
      cachedApiGet('/refdata/frequencies'),
      apiGet('/stakeholders/issuers/select').catch(() => ({ data: [] })),
      cachedApiGet('/refdata/calculation-profiles'),
      // T3-160: the contracts' Product Type list. All rows, so a note keeps a since-deactivated
      // type; the select offers active ones plus the note's own.
      apiGet('/refdata/investment-types?active_only=false').catch(() => ({ data: [] })),
    ]).then(([c, f, i, p, t]) => setRef((r) => ({
      ...r,
      countries: extractApiData(c).filter((x) => x.active !== false),
      frequencies: extractApiData(f).filter((x) => x.active !== false && Number(x.months_value) !== 998),
      issuers: extractApiData(i),
      profiles: extractApiData(p).filter((x) => x.active !== false),
      investmentTypes: extractApiData(t),
    }))).catch((err) => onError?.(extractApiError(err, 'Could not load reference data')));
  }, [active, onError]);
  useEffect(() => {
    if (!active || !issuerId) { setRef((r) => ({ ...r, programs: [] })); return; }
    // T3-160: programmes for the issuer AND the investment type, as the contract wizard filters them.
    const q = new URLSearchParams({ issuer_id: String(issuerId) });
    if (investmentTypeId) q.set('investment_type_id', String(investmentTypeId));
    apiGet(`/refdata/programs?${q}`)
      .then((res) => setRef((r) => ({ ...r, programs: extractApiData(res) })))
      .catch(() => setRef((r) => ({ ...r, programs: [] })));
  }, [active, issuerId, investmentTypeId]);
  return ref;
};

/** The seeded Simple Actual/365 profile reproduces the source workbook; it is the default. */
export const defaultProfileId = (profiles) =>
  (profiles.find((p) => p.system_key === 'SIMPLE_ACTUAL_365') || profiles[0] || {}).id || '';

/**
 * T3-158: a note has a SYSTEM reference (NTE-2026-0001, generated) and a USER reference
 * <3-letter prefix><DDMMYYYY of maturity>[B…] (e.g. FIN15052026). The prefix defaults to the
 * issuer's Notes reference prefix (Stakeholders → Issuer), else the tenant default.
 */
export const NOTE_PREFIX_PATTERN = /^[A-Z]{3}$/;

/** The 3-letter prefix of a user reference, or '' for a legacy code that does not follow the rule. */
export const prefixOfUserReference = (ref) =>
  (ref && /^[A-Z]{3}\d{8}[A-Z]?$/.test(ref) ? ref.slice(0, 3) : '');

/**
 * The user may also TYPE the whole reference (2026-10-06): letters and digits, max 20. Until they
 * do, the reference follows <prefix><DDMMYYYY of maturity>; once typed (reference_edited), what
 * they typed is used as-is (the server still adds B, C … if it is taken by a submitted note).
 */
export const USER_REFERENCE_PATTERN = /^[A-Z0-9]{1,20}$/;
const dateCode = (iso) => (iso ? `${iso.slice(8, 10)}${iso.slice(5, 7)}${iso.slice(0, 4)}` : '');

export const emptyNoteTerms = (settings, note) => {
  const prefix = prefixOfUserReference(note?.user_reference)
    || note?.issuer_note_reference_prefix || settings?.referencePrefix || '';
  // An existing note whose reference is not prefix + maturity (typed, suffixed or legacy) keeps it.
  const auto = note?.maturity_date ? `${prefix}${dateCode(note.maturity_date)}` : '';
  return {
  reference_prefix: prefix,
  user_reference: note?.user_reference || '',
  reference_edited: Boolean(note?.user_reference) && note.user_reference !== auto,
  gross_rate: note?.gross_rate ?? '',
  issue_date: note?.issue_date ?? '',
  maturity_date: note?.maturity_date ?? '',
  currency_id: note?.currency_id ?? '',
  interest_frequency_id: note?.interest_frequency_id ?? '',
  calculation_profile_id: note?.calculation_profile_id ?? '',
  capitalization_frequency_id: note?.capitalization_frequency_id ?? '',
  issuer_id: note?.issuer_id ?? '',
  program_id: note?.program_id ?? '',
  investment_type_id: note?.investment_type_id ?? '',
  description: note?.description ?? '',
  };
};

export const selectedProfileOf = (form, refData) =>
  (refData.profiles || []).find((p) => String(p.id) === String(form.calculation_profile_id)) || null;

/** The reference the note will carry: what the user typed, else <prefix><DDMMYYYY of maturity>. */
export const effectiveUserReference = (form) => (form.reference_edited
  ? String(form.user_reference || '').toUpperCase()
  : (NOTE_PREFIX_PATTERN.test(String(form.reference_prefix || '').toUpperCase()) ? noteReferenceOf(form) : ''));

/** Step 1 (Note Details) is complete. */
export const noteDetailsValid = (form) => Boolean(form.issue_date && form.maturity_date
  && form.issue_date < form.maturity_date && form.currency_id && form.investment_type_id
  && USER_REFERENCE_PATTERN.test(effectiveUserReference(form)));

/** Step 2 (Interest & Payments) is complete. */
export const noteInterestValid = (form, refData) => {
  const profile = selectedProfileOf(form, refData);
  return Boolean(form.gross_rate && Number(form.gross_rate) > 0 && Number(form.gross_rate) < 100
    && form.interest_frequency_id && profile
    && (profile.interest_method !== SPC || form.capitalization_frequency_id));
};

export const noteTermsValid = (form, refData) => noteDetailsValid(form) && noteInterestValid(form, refData);

export const noteReferenceOf = (form) => {
  const prefix = String(form.reference_prefix || '').toUpperCase();
  return form.maturity_date && prefix
    ? `${prefix}${form.maturity_date.slice(8, 10)}${form.maturity_date.slice(5, 7)}${form.maturity_date.slice(0, 4)}`
    : '';
};

/** The API payload for a note's terms. */
export const noteTermsPayload = (form, refData) => {
  const profile = refData ? selectedProfileOf(form, refData) : null;
  return {
    ...form,
    reference_prefix: String(form.reference_prefix || '').toUpperCase(),
    // Only a TYPED reference is sent; otherwise the server builds prefix + maturity (and suffixes).
    user_reference: form.reference_edited ? String(form.user_reference || '').toUpperCase() : null,
    reference_edited: undefined,
    issuer_id: form.issuer_id || null,
    program_id: form.program_id || null,
    investment_type_id: form.investment_type_id || null,
    capitalization_frequency_id: profile && profile.interest_method !== SPC ? null : (form.capitalization_frequency_id || null),
  };
};

/** Step 1 fields: identity of the note. */
export const NoteDetailsFields = ({ form, setForm, refData, disabled = false, systemReference = null }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const prefix = String(form.reference_prefix || '').toUpperCase();
  const edited = Boolean(form.reference_edited);
  const auto = NOTE_PREFIX_PATTERN.test(prefix) ? noteReferenceOf(form) : '';
  // Before a maturity date is entered the field shows just the issuer's prefix (e.g. SML), so the
  // user can type their own numbers straight after it (user, 2026-10-06).
  const value = edited ? (form.user_reference || '') : (auto || (NOTE_PREFIX_PATTERN.test(prefix) ? prefix : ''));
  const bad = edited && !USER_REFERENCE_PATTERN.test(value);
  const system = systemReference ? ` · System reference ${systemReference}` : '';
  // Choosing an issuer pre-fills its Notes reference prefix (it can still be typed over).
  const chooseIssuer = (e) => {
    const issuer = refData.issuers.find((i) => String(i.id) === String(e.target.value));
    setForm((f) => ({
      ...f, issuer_id: e.target.value, program_id: '',
      reference_prefix: issuer?.note_reference_prefix || f.reference_prefix,
    }));
  };
  return (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        {/* The whole reference can be typed (letters + digits, max 20). Until it is, it follows
            the issuer's prefix + the maturity date. */}
        <TextField label="Reference" value={value}
          placeholder={prefix ? `${prefix} + maturity date` : 'e.g. FIN15052026'}
          onChange={(e) => setForm((f) => ({
            ...f, reference_edited: true,
            user_reference: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20),
          }))}
          fullWidth required disabled={disabled} error={bad}
          slotProps={{
            htmlInput: { maxLength: 20 },
            input: edited && !disabled ? {
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={() => setForm((f) => ({ ...f, reference_edited: false, user_reference: '' }))}>
                    Default
                  </Button>
                </InputAdornment>
              ),
            } : undefined,
          }}
          helperText={bad ? 'Letters and digits only, up to 20 characters'
            : edited ? `Your own reference (letters/digits, max 20)${system}`
              : `${prefix ? `${prefix} (issuer prefix)` : 'Prefix'} + maturity date — type to use your own${system}`} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <TextField label="Start date" type="date" value={form.issue_date || ''} onChange={set('issue_date')}
          fullWidth required disabled={disabled} slotProps={{ inputLabel: { shrink: true } }}
          helperText="Shared by every investor (override per investor below)" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <TextField label="Maturity date" type="date" value={form.maturity_date || ''} onChange={set('maturity_date')}
          fullWidth required disabled={disabled} slotProps={{ inputLabel: { shrink: true } }}
          error={Boolean(form.issue_date && form.maturity_date && form.issue_date >= form.maturity_date)}
          helperText={form.issue_date && form.maturity_date && form.issue_date >= form.maturity_date
            ? 'Must be after the start date' : ' '} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <TextField select label="Currency" value={form.currency_id} onChange={set('currency_id')} fullWidth required disabled={disabled}>
          {refData.countries.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.currency_code} — {c.country}</MenuItem>
          ))}
        </TextField>
      </Grid>
      {/* T3-160: the contracts' Product Type list (Reference Data → Investment Types). Changing it
          clears the programme, whose list it filters, as in the contract wizard. */}
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <TextField select label="Investment Type" value={form.investment_type_id ?? ''} disabled={disabled}
          onChange={(e) => setForm((f) => ({ ...f, investment_type_id: e.target.value, program_id: '' }))}
          fullWidth required error={!form.investment_type_id}
          helperText={form.investment_type_id ? ' ' : 'Required to submit'}>
          {(refData.investmentTypes || [])
            .filter((t) => t.active !== false || String(t.id) === String(form.investment_type_id))
            .map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.type}{t.reference ? ` (${t.reference})` : ''}{t.active === false ? ' — inactive' : ''}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <TextField select label="Issuer (optional)" value={form.issuer_id} disabled={disabled}
          onChange={chooseIssuer} fullWidth>
          <MenuItem value="">None</MenuItem>
          {refData.issuers.map((i) => (
            <MenuItem key={i.id} value={i.id}>{i.name}{i.note_reference_prefix ? ` (${i.note_reference_prefix})` : ''}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <TextField select label="Programme (optional)" value={form.program_id} onChange={set('program_id')}
          fullWidth disabled={disabled || !form.issuer_id}>
          <MenuItem value="">None</MenuItem>
          {refData.programs.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField label="Description" value={form.description} onChange={set('description')}
          fullWidth multiline minRows={2} disabled={disabled} />
      </Grid>
    </Grid>
  );
};

/** Read-only description of a calculation profile (what the contract engine will apply). */
export const ProfileSummary = ({ profile }) => {
  if (!profile) return null;
  const rows = [
    ['Interest method', profile.interest_method],
    ['Day count', `${profile.day_count_convention || `Actual/${profile.day_count_basis}`} · ${profile.day_count_method || 'Inclusive'}`],
    ['Day count basis', profile.day_count_basis],
    ...(profile.compounding_frequency ? [['Compounding (per year)', profile.compounding_frequency]] : []),
  ];
  return (
    <Table size="small" sx={{ maxWidth: 520 }}>
      <TableBody>
        {rows.map(([k, v]) => (
          <TableRow key={k}>
            <TableCell sx={{ color: 'text.secondary', width: 200 }}>{k}</TableCell>
            <TableCell>{v}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/** Step 2 fields: how interest is calculated and paid. */
export const NoteInterestFields = ({ form, setForm, refData, disabled = false }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const profile = selectedProfileOf(form, refData);
  return (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <TextField label="Gross rate (%)" type="number" value={form.gross_rate} onChange={set('gross_rate')}
          fullWidth required disabled={disabled} slotProps={{ htmlInput: { step: '0.0001', min: 0 } }}
          helperText="Investors' net rate = gross rate less their commission spread" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <TextField select label="Calculation Profile" value={form.calculation_profile_id} disabled={disabled}
          onChange={(e) => setForm((f) => ({ ...f, calculation_profile_id: e.target.value, capitalization_frequency_id: '' }))}
          fullWidth required helperText="Determines interest method, day count and compounding — as for contracts">
          {(refData.profiles || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <TextField select label="Interest Payment Frequency" value={form.interest_frequency_id}
          onChange={set('interest_frequency_id')} fullWidth required disabled={disabled}>
          {refData.frequencies.map((f) => <MenuItem key={f.id} value={f.id}>{f.frequency}</MenuItem>)}
        </TextField>
      </Grid>
      {profile?.interest_method === SPC && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField select label="Capitalization Frequency" value={form.capitalization_frequency_id}
            onChange={set('capitalization_frequency_id')} fullWidth required disabled={disabled}
            helperText="Required for Simple Periodic Capitalization">
            {refData.frequencies.filter((f) => Number(f.months_value) < 998).map((f) => (
              <MenuItem key={f.id} value={f.id}>{f.frequency}</MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        <ProfileSummary profile={profile} />
      </Grid>
    </Grid>
  );
};

// -------------------------------------------------------------------------------------
// Maturity payout — ONE batch for the whole note (T3-153). The server records one row per
// investor from each investor's approved maturity-date schedule rows; the figures shown here are
// those approved figures, not editable inputs.
// -------------------------------------------------------------------------------------
export const PayoutDialog = ({ open, onClose, onSubmit, note, requireApproval }) => {
  const [form, setForm] = useState({ paid_date: '', payment_reference: '', notes: '' });
  const { saving, error, setError, submit } = useSubmit(onSubmit, onClose);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm({ paid_date: note?.maturity_date || '', payment_reference: '', notes: '' });
  }, [open, note, setError]);

  const legs = note?.legs || [];
  const sum = (k) => legs.reduce((s, l) => s + Number(k === 'nominal' ? l.nominal_amount : (l.figures?.[k] || 0)), 0);
  const money = (v) => formatNumber(v, 2);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Maturity payout — {note?.user_reference} ({note?.reference})</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Table size="small" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              {['Code', 'Investor', 'Capital', 'Net interest due', 'Total to pay', 'WHT payable', 'Commission'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600 }} align={['Code', 'Investor'].includes(h) ? 'left' : 'right'}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {legs.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.code}</TableCell>
                <TableCell>{l.investor_name}</TableCell>
                <TableCell align="right">{money(l.nominal_amount)}</TableCell>
                <TableCell align="right">{money(l.figures?.client_net_due)}</TableCell>
                <TableCell align="right">{money(l.figures?.total_due)}</TableCell>
                <TableCell align="right">{money(l.figures?.wht_amount)}</TableCell>
                <TableCell align="right">{money(l.figures?.commission_amount)}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ '& td': { fontWeight: 600, borderTop: 2, borderColor: 'divider' } }}>
              <TableCell colSpan={2}>Total ({legs.length} investor{legs.length === 1 ? '' : 's'})</TableCell>
              <TableCell align="right">{money(sum('nominal'))}</TableCell>
              <TableCell align="right">{money(sum('client_net_due'))}</TableCell>
              <TableCell align="right">{money(sum('total_due'))}</TableCell>
              <TableCell align="right">{money(sum('wht_amount'))}</TableCell>
              <TableCell align="right">{money(sum('commission_amount'))}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
          This closes the note: the server records the cash matched to each investor in Payment Matching → Notes
          (every coupon and the capital), which must already be fully matched. WHT still to be remitted stays owed.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Paid date" type="date" value={form.paid_date}
              onChange={(e) => setForm((x) => ({ ...x, paid_date: e.target.value }))}
              fullWidth required slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: note?.maturity_date } }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField label="Payment reference" value={form.payment_reference}
              onChange={(e) => setForm((x) => ({ ...x, payment_reference: e.target.value }))} fullWidth />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField label="Notes" value={form.notes} multiline minRows={2}
              onChange={(e) => setForm((x) => ({ ...x, notes: e.target.value }))} fullWidth />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={!form.paid_date || saving} onClick={() => submit(form)}>
          {saving ? 'Saving…' : requireApproval ? 'Record for approval' : 'Record payout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
